import React, { useState } from 'react';
import { capitalize } from 'lodash';
import {
  COURT_PAGE,
  ROOT_PAGE,
  getCityCourtsPageUrlWithSport,
  getCountryIndexPageUrlWithSport,
  getCountrySubdivisionIndexPageUrlWithSport,
} from 'constants/pages';
import Home from 'svg/Home';
import SearchIcon from 'svg/SearchIcon';
import SafeAreaPage from 'layouts/SafeAreaPage';
import Breadcrumbs from 'components/Breadcrumbs';
import Footer from 'components/Footer';
import Link from 'components/Link';
import SearchBox from 'components/forms/SearchBox';
import TopNav from 'components/nav/TopNav/TopNav';
import Head from 'components/utilities/Head';
import PopppinsFont from 'components/utilities/PoppinsFont';
import { Props } from './types';

export const generateBreadcrumbs = ({
  isForJsonLd,
  countryName,
  countrySlug,
  sport,
}: {
  isForJsonLd?: boolean;
  countryName: string;
  countrySlug: string;
  sport: string;
}) => {
  const sportName = sport ? capitalize(sport) : 'Pickleball';
  return {
    ui: isForJsonLd
      ? []
      : [
          {
            label: <Home className="w-4" />,
            url: ROOT_PAGE,
          },
          {
            label: countryName || '',
            url: '',
            isActivePage: true,
          },
        ],
    jsonLd: [
      {
        name: 'Home',
        id: ROOT_PAGE,
      },
      {
        name: `${sportName} in ${countryName}` || '',
        id: getCountryIndexPageUrlWithSport(countrySlug || '', sport),
      },
    ].map((item) => ({
      ...item,
      id: `${process.env.APP_URL}${item.id}`,
    })),
  };
};

export default function CitiesPage({ countryName, subdivisions, jsonLd, sport }: Props) {
  const [searchInput, setSearchInput] = useState('');
  const [citiesWithStates, setCitiesWithStates] = useState(subdivisions);
  const sportName = sport ? sport.charAt(0).toUpperCase() + sport.slice(1) : 'Pickleball';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue: string = e.target.value;
    setSearchInput(inputValue);

    const filteredCities = filterCitiesByName(subdivisions, inputValue);
    setCitiesWithStates(filteredCities);
  };

  function filterCitiesByName(data: Props['subdivisions'], cityName: string) {
    if (!cityName) {
      return data;
    }

    const filteredStates: Props['subdivisions'] = [];
    const lowerCaseCityName = cityName.toLowerCase();

    data.forEach((state) => {
      const filteredCities: any[] =
        state.cities?.filter((city) => city.name.toLowerCase().includes(lowerCaseCityName)) || [];

      if (filteredCities.length > 0) {
        // Create a new state object with filtered cities
        const filteredState: Props['subdivisions'][0] = {
          ...state,
          cities: filteredCities,
        };

        filteredStates.push(filteredState);
      }
    });

    return filteredStates;
  }

  return (
    <>
      <Head
        canonicalType="currentUrlStripQuery"
        title={`${sportName} in ${countryName}`}
        description={`Play ${sportName.toLowerCase()} in ${countryName}. Find all the courts, coaches, and lessons in your area.`}
      />
      <PopppinsFont />
      <SafeAreaPage isShowTopNav isHideSidebar>
        <div className="px-4 pt-4 lg:px-ds-2xl">
          <Breadcrumbs
            breadcrumbs={
              generateBreadcrumbs({
                countryName: countryName || '',
                countrySlug: '',
                sport: sport || 'pickleball',
              }).ui
            }
          />
        </div>
        <div className="flex grow flex-col bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary">
          <div className="relative flex w-full flex-col justify-between border-b border-color-border-input-lightmode px-4 py-4 dark:border-color-border-input-darkmode md:flex-row md:items-center lg:px-ds-2xl">
            <h1 className="typography-product-heading-compact my-2">
              {sportName} in {countryName}
            </h1>
            <div className="flex h-10 w-80 select-none items-center rounded-full bg-color-bg-input-lightmode-primary py-ds-xs pl-ds-md pr-ds-xs dark:bg-color-bg-input-darkmode-primary">
              <SearchIcon className="h-5 w-5 text-color-text-lightmode-placeholder dark:text-color-text-darkmode-placeholder" />
              <SearchBox
                searchInput={searchInput}
                handleInputChange={handleInputChange}
                placeholder="Search cities..."
                className="input-unstyled bg-color-bg-input-lightmode-primary"
              />
            </div>
          </div>
          <div className="px-4 py-ds-lg lg:px-ds-2xl">
            <p className="typography-product-body max-w-3xl text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              {`Find ${sport || 'pickleball'} courts and tournaments across ${countryName}. Browse cities nationwide to discover places to play, compete, and connect with local players.`}
            </p>
          </div>
          <div className="border-b border-color-border-input-lightmode dark:border-color-border-input-darkmode">
            <div className="px-4 pb-20 lg:px-ds-2xl">
              {citiesWithStates?.map((cityWithState, index) => (
                <div
                  key={index}
                  className="mt-10 border-b border-color-border-input-lightmode pb-10 dark:border-color-border-input-darkmode"
                >
                  <Link
                    href={getCountrySubdivisionIndexPageUrlWithSport(cityWithState.slug, sport)}
                  >
                    <h2 className="typography-product-heading-compact">{cityWithState.name}</h2>
                  </Link>
                  <ul className="mt-4 grid gap-4 xs:grid-cols-2 md:grid-cols-4 lg:grid-cols-5">
                    {cityWithState.cities?.map(({ name, id, slug }) => (
                      <Link href={getCityCourtsPageUrlWithSport(slug, sport)} key={id}>
                        <li className="typography-product-element-label">{name}</li>
                      </Link>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
        <Footer />
      </SafeAreaPage>
      {!!jsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />
      )}
    </>
  );
}
